import { useMemo, useState } from "react";
import { useNeural } from "@/store/neuralStore";
import { Brain, Plus, Search, Sparkles, Tag, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const KIND_HUE: Record<string, number> = { note: 195, vector: 270, fact: 45, lesson: 145 };

export function SecondBrain() {
  const memory = useNeural((s) => s.memory);
  const addMemory = useNeural((s) => s.addMemory);
  const removeMemory = useNeural((s) => s.removeMemory);

  const [q, setQ] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tags, setTags] = useState("");
  const [kind, setKind] = useState<"note" | "vector" | "fact" | "lesson">("note");

  const filtered = useMemo(() => {
    if (!q) return memory;
    const ql = q.toLowerCase();
    return memory.filter(
      (m) =>
        m.title.toLowerCase().includes(ql) ||
        m.body.toLowerCase().includes(ql) ||
        m.tags.some((t) => t.toLowerCase().includes(ql)),
    );
  }, [memory, q]);

  const stats = useMemo(() => {
    const by: Record<string, number> = {};
    memory.forEach((m) => (by[m.kind] = (by[m.kind] ?? 0) + 1));
    return by;
  }, [memory]);

  return (
    <div className="h-full overflow-y-auto p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2">
            <Brain className="w-6 h-6 text-primary" /> Second Brain
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Long-term knowledge · semantic search · auto-organized · self-improving loop
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(["note", "vector", "fact", "lesson"] as const).map((k) => (
            <div
              key={k}
              className="text-[10px] font-mono px-2 py-1 rounded border"
              style={{ borderColor: `hsl(${KIND_HUE[k]} 80% 35% / 0.5)`, color: `hsl(${KIND_HUE[k]} 95% 70%)` }}
            >
              {stats[k] ?? 0} {k}
            </div>
          ))}
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Semantic search across all memory..." className="h-10 pl-9" />
      </div>

      <div className="glass rounded-xl p-4 space-y-2">
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Add memory</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <select value={kind} onChange={(e) => setKind(e.target.value as any)} className="h-9 px-2 rounded bg-surface-2 border border-border text-sm">
            <option value="note">note</option>
            <option value="fact">fact</option>
            <option value="lesson">lesson</option>
            <option value="vector">vector</option>
          </select>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className="h-9 md:col-span-2" />
          <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="comma,tags" className="h-9" />
        </div>
        <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Knowledge body..." className="h-9" />
        <Button
          size="sm"
          onClick={() => {
            if (!title) return;
            addMemory({
              kind,
              title,
              body,
              tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
              importance: 60,
            });
            setTitle("");
            setBody("");
            setTags("");
          }}
          className="bg-gradient-neural text-primary-foreground border-0 gap-1.5"
        >
          <Plus className="w-3.5 h-3.5" /> Save to Brain
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((m) => (
          <div
            key={m.id}
            className="glass rounded-xl p-4 hover:border-primary/40 transition-all relative"
            style={{ boxShadow: `inset 0 0 0 1px hsl(${KIND_HUE[m.kind]} 70% 30% / 0.3)` }}
          >
            <div className="flex items-start justify-between mb-2">
              <div
                className="text-[10px] font-mono px-1.5 py-0.5 rounded border"
                style={{ borderColor: `hsl(${KIND_HUE[m.kind]} 80% 35% / 0.5)`, color: `hsl(${KIND_HUE[m.kind]} 95% 70%)` }}
              >
                {m.kind}
              </div>
              <button onClick={() => removeMemory(m.id)} className="text-muted-foreground hover:text-destructive">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="font-display font-semibold text-sm mb-1">{m.title}</div>
            <p className="text-xs text-muted-foreground line-clamp-3 mb-3">{m.body}</p>
            <div className="flex items-center justify-between">
              <div className="flex flex-wrap gap-1">
                {m.tags.map((t) => (
                  <span key={t} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-surface-3 border border-border text-muted-foreground flex items-center gap-1">
                    <Tag className="w-2 h-2" /> {t}
                  </span>
                ))}
              </div>
              <span className="text-[9px] font-mono text-muted-foreground flex items-center gap-1">
                <Sparkles className="w-2.5 h-2.5 text-secondary" /> {m.importance}
              </span>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-xs text-muted-foreground p-8 border border-dashed border-border rounded-lg">
            No memories match your search.
          </div>
        )}
      </div>
    </div>
  );
}
